/**
 * Permission Guard Component
 * 
 * Protects routes from unauthorized access based on user permissions.
 * Redirects to dashboard or shows access denied message if user lacks permission.
 */

import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePermissions } from '@/providers/PermissionsProvider';
import { Shield } from 'lucide-react';

interface PermissionGuardProps {
  readonly children: React.ReactNode;
  readonly screenKey?: string;
  readonly subPermissionKey?: string;
  readonly fallbackPath?: string;
  readonly showAccessDenied?: boolean;
}

export function PermissionGuard({
  children,
  screenKey,
  subPermissionKey,
  fallbackPath = '/dashboard',
  showAccessDenied = false,
}: PermissionGuardProps): React.ReactElement | null {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, hasSubPermission, canAccessRoute, loading, permissionsEnforced } = usePermissions();

  // Determine if user has access
  const hasAccess = (() => {
    if (!permissionsEnforced) return true;
    if (loading) return true; // Allow while loading
    
    if (screenKey) {
      return hasPermission(screenKey);
    }
    
    if (subPermissionKey) {
      return hasSubPermission(subPermissionKey);
    }
    
    // Check by current route
    return canAccessRoute(location.pathname);
  })();

  useEffect(() => {
    if (!loading && !hasAccess && !showAccessDenied) {
      navigate(fallbackPath, { replace: true });
    }
  }, [hasAccess, loading, navigate, fallbackPath, showAccessDenied]);

  // While loading, show nothing or a loading state
  if (loading) {
    return <>{children}</>;
  }

  // If no access and showing access denied message
  if (!hasAccess && showAccessDenied) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-8">
        <div className="flex flex-col items-center text-center max-w-md">
          <div className="rounded-full bg-muted p-4 mb-4">
            <Shield className="h-8 w-8 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground mb-4">
            You don't have permission to access this feature. Please contact your administrator if you believe this is an error.
          </p>
          <button
            onClick={() => navigate(fallbackPath)}
            className="text-primary hover:underline"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // If no access and not showing message, return null (redirect will happen)
  if (!hasAccess) {
    return null;
  }

  return <>{children}</>;
}

/**
 * Higher-order component for protecting routes
 */
export function withPermissionGuard<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  screenKey?: string,
  subPermissionKey?: string
) {
  return function PermissionGuardedComponent(props: P) {
    return (
      <PermissionGuard screenKey={screenKey} subPermissionKey={subPermissionKey}>
        <WrappedComponent {...props} />
      </PermissionGuard>
    );
  };
}
